import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAdmin } from '../hooks/useAdmin';
import { GEO_CONFIG } from '../config/geo';

interface GeoGateProps {
  children: React.ReactNode;
  allowedCountries: readonly string[];
}

export function GeoGate({ children, allowedCountries: defaultAllowedCountries }: GeoGateProps) {
  const [isAllowed, setIsAllowed] = useState<boolean>(true);  // Default to allowed
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAdmin } = useAdmin();

  useEffect(() => {
    async function checkLocation() {
      try {
        // If user is admin or geo-blocking is disabled globally, bypass geo-blocking
        if (isAdmin || !GEO_CONFIG.enabled) {
          setIsAllowed(true);
          setIsLoading(false);
          return;
        }

        // Get geo configuration from localStorage
        const savedGeoConfig = localStorage.getItem('trinity_geo_config')
        const geoConfig = savedGeoConfig ? JSON.parse(savedGeoConfig) : { enabled: GEO_CONFIG.enabled, allowedCountries: defaultAllowedCountries }

        // If geo-blocking is disabled, allow access
        if (!geoConfig.enabled) {
          setIsAllowed(true);
          setIsLoading(false);
          return;
        }

        // Get user's location from IP using a free geolocation API
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        if (data.error) {
          throw new Error('Failed to get location');
        }

        // Check if user's country is allowed
        const isCountryAllowed = geoConfig.allowedCountries.includes(data.country_code);
        setIsAllowed(isCountryAllowed);
        
        if (!isCountryAllowed) {
          setError(`Access not available in ${data.country_name}`);
        }
      } catch (err) {
        console.error('Geolocation error:', err);
        // On error, allow access by default for better UX
        setIsAllowed(true);
        setError(null);
      } finally {
        setIsLoading(false);
      }
    }

    checkLocation();
  }, [defaultAllowedCountries, isAdmin]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-white text-center"
        >
          <div className="mb-4">Verifying your location...</div>
          <div className="w-8 h-8 border-t-2 border-blue-500 rounded-full animate-spin mx-auto"></div>
        </motion.div>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-red-900/20 rounded-lg text-center max-w-md"
        >
          <h2 className="text-xl font-bold text-red-400 mb-4">Access Restricted</h2>
          <p className="text-white/80">{error}</p>
          <p className="mt-4 text-sm text-white/60">
            This service is only available in specific regions.
          </p>
        </motion.div>
      </div>
    );
  }

  return <>{children}</>;
} 